import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message, Card, Input, Alert, Image } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  BarsOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import GovernmentDocForm from "./governmentDocumentForm";
import dayjs from "dayjs";
import CommonPagination from "../../components/common/pagination";

const GovernmentDoc = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [governmentDoc, setGovernmentDoc] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [detailData, setDetailData] = useState();
  const [openDetail, setOpenDetail] = useState();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);

  const fieldLabels = {
    title: "Name",
    image: "Image",
    file_url: "Document",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const fetchGovernmentDoc = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/governmentDoc?page=${pageNumber}&limit=${limit}&search=${searchText}`,
      );
      setGovernmentDoc(res.data.data.rows || []);
      setTotal(res.data.data.count || 0);
    } catch (error) {
      console.error("FetchGovernmentDoc error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGovernmentDoc(page);
  }, [page, searchText]);

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(
          `${BASE_URL}/api/governmentDoc/${editData.id}`,
          formData,
        );
        setSuccessMsg("Government Document updated");
      } else {
        await axios.post(`${BASE_URL}/api/governmentDoc`, formData);
        setSuccessMsg("Government Document Added");
      }
      setTimeout(() => setSuccessMsg(null), 3000);
      setOpen(false);
      setEditData(null);
      fetchGovernmentDoc(1);
    } catch {
      message.error("Failed to submit governmentDoc");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/governmentDoc/${id}`);
      setSuccessMsg("Government Document deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchGovernmentDoc(page);
    } catch {
      message.error("Failed to delete governmentDoc");
    }
  };

  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    { title: "Name", dataIndex: "title" },
    {
      title: "Image",
      dataIndex: "image",
      render: (image) =>
        image ? (
          <img
            src={`${BASE_URL}${image}`}
            alt="government"
            width={60}
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : (
          "No Image"
        ),
    },
    {
      title: "Document",
      dataIndex: "file_url",
      render: (file_url) =>
        file_url ? (
          <a
            href={`${BASE_URL}${file_url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View File
          </a>
        ) : (
          "No file"
        ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            style={{ borderColor: "#ec4899", color: "#ec4899", marginRight: 8 }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData(record);
              setOpenDetail(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData({
                ...record,
                image: [
                  {
                    uid: "-1",
                    name: "image.jpg",
                    status: "done",
                    url: `${BASE_URL}${record.image}`,
                  },
                ],
                file_url: [
                  {
                    uid: "-1",
                    name: "file",
                    status: "done",
                    url: `${BASE_URL}${record.file_url}`,
                  },
                ],
              });
              setOpen(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#374151",
              borderColor: "#374151",
              color: "#fff",
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title:
                  "Are you sure you want to delete this Government Document?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="Government Document">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
            backgroundColor: "#f6ffed",
            color: "#389e0d",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search Government Document.."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          style={{
            backgroundColor: "#ec4899",
            borderColor: "#ec4899",
            color: "#fff",
          }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add Government Document
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={governmentDoc}
        rowKey="id"
        loading={loading}
        pagination={false}
        style={{ marginTop: 20 }}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        destroyOnClose
      >
        <GovernmentDocForm
          initialValues={editData}
          onSuccess={handleSubmit}
          editMode={!!editData}
        />
      </Modal>

      <Modal
        title="Government Document Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={[
          <Button
            key="close"
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
            }}
            onClick={() => setOpenDetail(false)}
          >
            Close
          </Button>,
        ]}
      >
        <Table
          dataSource={Object.entries(detailData ?? {})
            .filter(([key]) => key !== "id")
            .map(([key, value]) => {
              let field = fieldLabels[key] || key;
              let displayValue = value;

              if (key === "image" && value) {
                let src = value.url || value;
                if (src && !src.startsWith("http")) src = `${BASE_URL}${src}`;
                displayValue = (
                  <Image
                    src={src}
                    width={100}
                    style={{ borderRadius: 4 }}
                    preview={{
                      mask: (
                        <EyeOutlined style={{ color: "white", fontSize: 18 }} />
                      ),
                    }}
                  />
                );
              }

              if (key === "file_url" && value) {
                let fileSrc = value.url || value;
                if (fileSrc && !fileSrc.startsWith("http"))
                  fileSrc = `${BASE_URL}${fileSrc}`;
                displayValue = (
                  <a href={fileSrc} target="_blank" rel="noopener noreferrer">
                    View File
                  </a>
                );
              }

              if ((key === "created_at" || key === "updated_at") && value) {
                displayValue = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
              }

              if (!displayValue) displayValue = "-";
              return { key, field, value: displayValue };
            })}
          columns={[
            {
              title: "Field",
              dataIndex: "field",
              key: "field",
              width: "30%",
              render: (text) => <strong>{text}</strong>,
            },
            { title: "Value", dataIndex: "value", key: "value" },
          ]}
          pagination={false}
          bordered
        />
      </Modal>
    </Card>
  );
};

export default GovernmentDoc;
